import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

import LicenceDocument from "@/components/LicenceDocument";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import type { LicenceCertificate } from "@/services/licences.service";
import { getGenericLicenceTerms, toPublicLicenceSections } from "@/services/licences.service";
import { colors, fonts, fontSizes } from "@/styles/design-system";

import ModalScrollArea from "./ModalScrollArea";
import {
  CloseButton,
  ModalContainer,
  ModalContent,
  ModalHead,
  ModalHeading,
  Overlay,
} from "./styles";

/**
 * The YouTube Licence Terms — the terms a video is whitelisted on, shown before
 * purchase. Built on the shared phone-modal template
 * (docu/modal-template-phone.md), variant (b): fixed head holds only the title
 * + ✕, everything scrolls below, no bottom button — matching the
 * "Choose Your Option" (LicenseModal) modal family.
 *
 * Content is the LIVE generic template, fetched from the backend and rendered
 * with the same block renderer (`LicenceDocument`) AND the same public-preview
 * transform (`toPublicLicenceSections`) as the /licence-terms page, so the
 * modal, the page, the certificate and the PDF can never drift. Fetched once,
 * then cached across opens.
 */

/* Title + intro as ONE panel, so the framing of the document reads as a single
   block rather than a heading floating above a paragraph.
   Solid, not translucent: the document scrolls underneath the modal's own
   background, and a see-through panel let the text behind it show as the page
   moved. */
const IntroPanel = styled.div`
  background: ${colors.brown.darkest};
  border: 1px solid ${colors.border.light};
  border-radius: 6px;
  padding: 16px 18px;
  margin: 0 0 24px;
  text-align: left;
`;

/* White here, overriding the shared ModalHeading's peach — that component is
   used by three modals and this is the only one that wanted it changed, so the
   override is local rather than a change to the shared style. */
const PanelHeading = styled(ModalHeading)`
  color: #ffffff;
  margin-bottom: 10px;
`;

/* Intro blurb — muted body text, same wording as the public page. */
const IntroText = styled.p`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.65;
  color: ${colors.text.muted};
  margin: 0;
`;

/* The document body: one consistent body font / size / colour, left-aligned —
   so LicenceDocument's own section titles and clauses read as ONE document
   instead of inheriting the modal's mixed fonts. Mirrors the page's `Doc`. */
const Doc = styled.div`
  text-align: left;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  line-height: 1.62;
  color: ${colors.text.body};
`;

const Muted = styled.div`
  font-family: ${fonts.body};
  color: ${colors.text.muted};
`;

let cache: LicenceCertificate["rendered"]["sections"] | null = null;

const LicenseTermsModal: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { mounted, closing } = useModalAnimation(open);
  const [sections, setSections] = useState<LicenceCertificate["rendered"]["sections"] | null>(cache);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || cache) return;
    getGenericLicenceTerms("single_licence")
      .then((r) => {
        const publicSections = toPublicLicenceSections(r.sections);
        cache = publicSections;
        setSections(publicSections);
      })
      .catch(() => setError(true));
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <ModalContainer $closing={closing} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <CloseButton onClick={onClose} aria-label="Close">
          &#x2715;
        </CloseButton>
        {/* The head keeps its height so the ✕ has room, but the title has moved
            into the panel below with the paragraph it belongs to. The gold rule
            went with it — it existed to separate a title from the content, and
            the panel's own edge does that now. */}
        <ModalHead />

        <ModalScrollArea>
          <ModalContent style={{ paddingTop: 4 }}>
            <IntroPanel>
              <PanelHeading>YouTube Licence Terms</PanelHeading>
              <IntroText>
                These are the terms every whitelisted YouTube video is licensed on. This is the
                generic preview version — the certificate you receive when a claim is released fills
                in your own licence number, track, video and channel.
              </IntroText>
            </IntroPanel>

            {error && (
              <Muted>These terms could not be loaded right now. Please try again shortly.</Muted>
            )}
            {!error && !sections && <Muted>Loading…</Muted>}
            {sections && (
              <Doc>
                <LicenceDocument sections={sections} />
              </Doc>
            )}
          </ModalContent>
        </ModalScrollArea>
      </ModalContainer>
    </Overlay>,
    document.body,
  );
};

export default LicenseTermsModal;
